<template>
	<div class="container-fluid">
		<page-loading v-if="!data_loaded" />
		<template v-else>
			<div v-if="show_nudge" class="col-12 text-center mb-5">
				<div class="mx-auto rounded-circle circle-cropped avatar-xl bg-secondary text-white text-5xl mb-4 mt-5">
		    		<i class="fad fa-fire-alt fa-fw"></i>
		    	</div>
		    	<div class="font-weight-bold text-2xl text-gray-700">
		    		Add a new campaign
		    	</div>
		    	<div class="font-lg text-gray-500 mb-3">
		    		Manage your outreach events, mailers, and other marketing campaigns
		    	</div>
		    	<span class="btn btn-primary btn-lg" data-toggle="modal" data-target="#campaignCreateModal" data-test="addCampaignButton">Create Campaign</span>
		    </div>
		</template>

		<div v-show="data_loaded" class="row">
			<div class="col-12">
			    <div class="d-flex align-items-center">
			    	<h1>Campaigns</h1>
			        <div class="flex-grow-1 ml-3 mr-2">
			        	<select class="form-control form-control-sm" v-model="showType" style="max-width: 200px" data-test="campaignsFilter">
			        		<option value="active">Active</option>
			        		<option value="complete">Complete or Archived</option>
			        		<option value="all">All Campaigns</option>
			        	</select>
			        </div>

					<span
						v-if="perms.can_create_campaign"
						v-tooltip.bottom="'Add Campaign'"
						class="btn btn-sm btn-ghost-primary text-gray-500 cursor-pointer leading-none"
						data-toggle="modal" data-target="#campaignCreateModal">
						<i class="far fa-plus-circle text-xl"></i>
					</span>
			    </div>


				<div class="card campaigns-table table-responsive" :class="config.layout">
					<vuetable
						ref="vuetable"
						api-url="/api/campaigns"
						data-path="data"
						pagination-path=""
						:fields="fields"
						:css="css.table"
						row-class="drop-down-hover h-100"
						:sort-order="sortOrder"
						:sort-params="getSortParam"
						:append-params="moreParams"
						:no-data-template="emptyText"
						@vuetable:load-success="onDataLoaded"
						@vuetable:pagination-data="onPaginationData">

						<template slot="title" slot-scope="props">
							<div class="text-sm text-gray-700 mb-1">
								{{ props.rowData.status_text }}
								<span v-if="props.rowData.status < 2 && props.rowData.complete_by && moment(props.rowData.complete_by) < moment()" class="badge badge-danger">overdue</span>
								<i v-if="props.rowData.status == 2" class="fad fa-badge-check text-primary text-base"></i>
							</div>

							<div class="truncated_name">
								<router-link :to="{ name: 'campaigns.show', params: { id: props.rowData.id } }">{{ props.rowData.title }}</router-link>
								<span class="badge bg-secondary text-white">{{ props.rowData.type_readable }}</span>
							</div>

							<div v-if="config.layout === 'mobile'" class="mt-2 mb-1" style="width:120px;">
	                        	<div class="d-flex justify-content-between text-sm font-weight-bold">
	                            	<span class="text-gray-700">{{ props.rowData.visits.length }} <span class="font-weight-normal text-gray-500">visits</span></span>
									<span>{{ props.rowData.progress }}%</span>
	                        	</div>

			                    <div class="progress progress-xs">
			                        <div class="progress-bar h-100" :class="props.rowData.status == 2 ? 'bg-green-500' : 'bg-primary'" role="progressbar" :style="'width:' + props.rowData.progress + '%'"></div>
			                    </div>	                        
		                    </div>
						</template>

						<template v-if="config.layout === 'desktop'" slot="progress" slot-scope="props">
							<div class="d-flex justify-content-between text-sm font-weight-bold">
								<span class="text-gray-700">{{ props.rowData.visits.length }} <span class="font-weight-normal text-gray-500">visits</span></span>
								<span>{{ props.rowData.progress }}%</span>
							</div>

							<div class="progress progress-xs mb-1">
								<div class="progress-bar h-100" :class="props.rowData.status == 2 ? 'bg-green-500' : 'bg-primary'" role="progressbar" :style="'width:' + props.rowData.progress + '%'"></div>
							</div>
						</template>

						<template slot="complete_by" slot-scope="props">
							<div class="d-flex flex-column justify-content-between h-100">
								<div v-if="config.layout === 'mobile'" class="drop-down dropleft">
									<a href="#" role="button" id="dropdownMenuLink" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
										<i class="far fa-ellipsis-h text-xl"></i>
									</a>
									<div class="dropdown-menu dropdown-menu-right py-0" aria-labelledby="dropdownMenuLink">
										<router-link :to="{ name: 'routes.show', params: { id: props.rowData.id } }" class="dropdown-item" data-test="showCampaignMobile">Open Campaign</router-link>
										<span v-if="props.rowData.can.delete" class="dropdown-item" @click="deleteCampaign(props.rowData.id)" data-test="deleteCampaignMobile">Delete Campaign</span>
									</div>
								</div>

								<div :class="{ 'mb-1' : config.layout === 'mobile' }">
									<div class="mb-1" v-if="props.rowData.ended_on">
										<span v-html="config.layout === 'desktop' ? 'completed' : 'on'" /> <span class="text-gray-700">{{ moment(props.rowData.ended_on).format("MMM D, YYYY") }}</span>
									</div>
									<div class="mb-1" v-else>
										<span v-if="config.layout === 'desktop'">complete</span> by <span class="text-gray-700">{{ props.rowData.complete_by ? props.rowData.complete_by : '--' }}</span>
									</div>

									<div v-if="props.rowData.assigned_user" class="d-flex justify-content-end align-items-center text-gray-700">
										<div v-if="config.layout === 'desktop'" class="rounded-circle circle-cropped avatar-xs mr-2">
											<img :src="props.rowData.assigned_user.photo_url" class="img-fluid">
										</div>
										{{ props.rowData.assigned_user.display_name }}
										<div v-if="config.layout === 'mobile'" class="rounded-circle circle-cropped avatar-xs ml-1">
											<img :src="props.rowData.assigned_user.photo_url" class="img-fluid">
										</div>			                        
									</div>
								</div>
							</div>
						</template>


						<template v-if="config.layout == 'desktop'" slot="actions" slot-scope="props">
							<div class="drop-down dropleft">
								<a href="#" role="button" id="dropdownMenuLink" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
									<i class="far fa-ellipsis-h text-xl"></i>
								</a>
								<div class="dropdown-menu dropdown-menu-right py-0" aria-labelledby="dropdownMenuLink">
									<router-link :to="{ name: 'campaigns.show', params: { id: props.rowData.id } }" class="dropdown-item" data-test="showCampaign">Open Campaign</router-link>
									<span v-if="props.rowData.can.delete" class="dropdown-item" @click="deleteCampaign(props.rowData.id)" data-test="deleteCampaign">Delete Campaign</span>
								</div>
							</div>						
						</template>
					</vuetable>
				</div>

				<div :class="$refs.vuetable && $refs.vuetable.tableData && $refs.vuetable.tableData.length > 0 ? 'd-flex' : 'd-none'" class="background-fix vuetable-pagination mb-3">
					<vuetable-pagination-info
						ref="paginationInfo"
						class="flex-fill"
						info-class="pagination-info"
						info-template="{from} to {to} of {total} items" />

					<vuetable-pagination-bootstrap
						ref="pagination"
						:css="css.pagination"
						class="flex-fill text-right"
						@vuetable-pagination:change-page="onChangePage" />
				</div>
			</div>
		</div>

		<campaign-create-modal />
	</div>
</template>

<script>
	import { mapState } from 'vuex'
    import BreadcrumbsManager from '../../helpers/BreadcrumbManager';
	import CampaignCreateModal from '../../components/CampaignCreateModal';
	import Vuetable from 'vuetable-2'
	import VuetablePaginationInfo from 'vuetable-2/src/components/VuetablePaginationInfo'
	import VuetablePaginationBootstrap from '../../components/VuetablePaginationBootstrap.vue'


    export default {
    	mixins: [BreadcrumbsManager],

    	components: {
    		CampaignCreateModal,
			Vuetable,
			VuetablePaginationInfo,
			VuetablePaginationBootstrap,
    	},

        data () {
            return {
                data_loaded: false,
                load_count: 0,
                show_nudge: false,

                fields: [
					{
						name: 'title',
						title: 'Title',
						sortField: 'title',
						dataClass: 'field-title',
					},
					{
						name: 'progress',
						title: 'Progress',
						dataClass: 'align-bottom pb-35',
						width: '120px',
	                	titleClass: 'd-none d-md-table-cell',
	                	dataClass: 'd-none d-md-table-cell align-bottom',
					},
					{
						name: 'complete_by',
						title: 'Date',
						sortField: 'complete_by',
						dataClass: 'h-100 text-sm text-gray-500 text-right',
						titleClass: 'text-right',
						//width: '180px',
					},
	                {
	                	name: 'actions',
	                	title: '',
	                	dataClass: 'text-right',
	                	width: '30px',
	                	titleClass: 'd-none d-md-table-cell',
	                	dataClass: 'd-none d-md-table-cell'
	                },
                ],

                emptyText: 'Loading...please wait',

				css: {
					table: {
						tableClass: 'table campaigns-table',
						ascendingIcon: 'fas fa-sort-down',
						descendingIcon: 'fas fa-sort-up',
					},

					pagination: {
						wrapperClass: 'pagination',
						activeClass: 'active',
						disabledClass: 'disabled',
						pageClass: 'page',
						linkClass: 'link',
						icons: {
							first: '',
							prev: '',
							next: '',
							last: '',
						},
					},

					icons: {
						first: 'fas fa-angle-double-left',
						prev: 'fas fa-angle-left',
						next: 'fas fa-angle-right',
						last: 'fas fa-angle-double-right',
					},
				},

				sortOrder: [
					{
						field: 'id',
						direction: 'desc',
					}
				],

				moreParams: {
					'filter[status_group]': 'active',
				},

				showType: 'active',
            }
        },

        created() {
        },
        
        activated() {
            this.setBreadcrumbs([
                { text: 'Home', to: { name: 'home' } },
                { text: 'Campaigns', to: { name: 'campaigns.index' } },
            ]);
			if (this.load_count > 0) {
				this.$refs.vuetable.refresh();
			}
        },

        mounted() {
        	this.$root.$on('campaign_added', () => {
        		this.data_loaded = false;
        		Vue.nextTick( () => this.$refs.vuetable.refresh() )
        	});
        },

        methods: {
            deleteCampaign(id) {
				Swal.fire({
					title: 'Are you sure?',
					text: 'You will not be able to recover this campaign. Check-ins made on this campaign will be kept.',
					icon: 'warning',
					showCancelButton: true,
					confirmButtonText: 'Delete campaign',
					cancelButtonText: 'Keep campaign',
					animation: false,
                }).then((result) => {
                	if (result.value) {
                        axios.delete('/api/campaigns/' + id, id).then(() => {
                            this.$store.dispatch('refreshCampaigns');
                            Vue.nextTick( () => this.$refs.vuetable.refresh() )
                        })
                    }
                });
            },

			getSortParam: function(sortOrder) {
				return sortOrder.map(function(sort) {
					return (sort.direction === 'desc' ? '-' : '') + sort.field
				}).join(',')
			},

			onChangePage(page) {
				this.$refs.vuetable.changePage(page);
			},

			onDataLoaded() {
				this.data_loaded = true;
				this.load_count = this.load_count + 1;
				Vue.nextTick( () => {
					if (this.$refs.vuetable.tableData.length == 0) {
						this.emptyText = 'No records found';

						if (this.showType == 'all' || (this.showType != 'complete' && this.load_count == 1)) {
							this.show_nudge = true;
						} else {
							this.show_nudge = false;
						}
					} else {
						this.show_nudge = false;
					}
				});          
			},

			onPaginationData(paginationData) {
				this.$refs.pagination.setPaginationData(paginationData)
				this.$refs.paginationInfo.setPaginationData(paginationData)
			},						        	             
        },

        computed: {
        	...mapState(['config', 'perms'])
        },

        watch: {
		    showType() {
				this.moreParams = {
					'filter[status_group]': this.showType
				}
				Vue.nextTick( () => this.$refs.vuetable.refresh() )
		    },
        },
    }
</script>